import * as React from "react";
import Checkbox from "@uikit/checkbox";
import {
  CourseClosure,
  NotifyMeFormSubmission,
  RateEnquiryFormSubmission,
} from "@kalabo/golfsavers-common";
import _, { set } from "lodash";
import { Alert, Button, Text } from "@uikit";
import Textbox from "@uikit/textbox";
import Choice from "@uikit/choice";
import Phone from "@uikit/phone";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import COUNTRIES from "../../../../constants/countries";
import { RangePickerProps } from "antd/es/date-picker";
import useUser from "@hooks/useUser";
import { toFriendlyDate, invoke } from "@kalabo/golfsavers-common";
import Notify_Icon from "@images/icons/notify_icon.svg";
import { CheckCircleIcon } from "@heroicons/react/outline";
import { useApp } from "@store/app";
import { getMailingListTags } from "@utilities/core";
import DateField from "@uikit/datefield";

dayjs.extend(utc);

interface RateEnquiryProps {
  course_id: string;
  hide_request_quote?: boolean;
  last_contract_date: string;
  date: string;
  closure?: CourseClosure;
}

const RateEnquiry = ({
  course_id,
  hide_request_quote = false,
  last_contract_date,
  date,
  closure,
}: RateEnquiryProps) => {
  const [showForm, setShowForm] = React.useState(false);
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [processing, setProcessing] = React.useState(false);

  let number_of_golfers = useApp(($) => $.golfers);
  const setDate = useApp(($) => $.setDate);

  const [formData, setFormData] = React.useState({
    date: date ? dayjs.utc(date).startOf("day").toDate() : dayjs().toDate(),
    course_id: course_id,
    agent_id: process.env.GATSBY_AGENT_ID,
    create_account: false,
    join_mailing_list: true,
  } as RateEnquiryFormSubmission);

  const [notifyMeData, setNotifyMeData] = React.useState({
    date: dayjs.utc(date).toDate(),
    course_id: course_id,
    agent_id: process.env.GATSBY_AGENT_ID,
    email: "",
    join_mailing_list: true,
    tags: getMailingListTags(),
    source_url: window.location.href,
    type: closure ? "CLOSURE" : "RATE", // defines if it is a closure notification or a rate enquiry notification
  } as NotifyMeFormSubmission);

  const { user } = useUser();

  React.useEffect(() => {
    setFormData({
      ...formData,
      first_name: user?.first_name,
      last_name: user?.last_name,
      email: user?.email,
      phone: user?.phone,
      country_of_residence: user?.country_of_residence,
    });
    setNotifyMeData({
      ...notifyMeData,
      email: user?.email,
      join_mailing_list: user ? false : true,
    });
  }, [user]);

  React.useEffect(() => {
    setFormData({
      ...formData,
      number_of_golfers: number_of_golfers,
    });
  }, [number_of_golfers]);

  React.useEffect(() => {
    setFormData({
      ...formData,
      number_of_golfers: number_of_golfers,
    });
  }, [number_of_golfers]);

  // React.useEffect(() => {
  //   if (showSuccess) {
  //     setTimeout(() => {
  //       setShowSuccess(false);
  //       setProcessing(false);
  //     }, 5000);
  //   }
  // }, [showSuccess]);

  // get a list of options for preferred time starting at 6am till 8pm in 60 minute intervals in format e.g 06:00 to 07:00 with a value of the same
  const preferredTimeOptions = _.times(14, (i) => {
    const hour = i + 6;
    const hourString = hour.toString().padStart(2, "0");
    const hourNextString = (hour + 1).toString().padStart(2, "0");
    return {
      label: `${hourString}:00 - ${hourNextString}:00`,
      value: `${hourString}:00 - ${hourNextString}:00`,
    };
  });

  const isEndOfDayClosure = React.useMemo(() => {
    // if the closure end date is set and the end_time is EQUAL or AFTER 23:55 then it is an end of day closure
    return (
      closure?.end_date &&
      closure?.end_time &&
      dayjs(closure?.end_time, "HH:mm").isSameOrAfter(dayjs("23:55", "HH:mm"))
    );
  }, [closure]);

  const isNotifyMeValid = React.useMemo(() => {
    return notifyMeData.date && notifyMeData.email && notifyMeData.course_id;
  }, [notifyMeData]);
  const isValid = React.useMemo(() => {
    return (
      formData.date &&
      formData.number_of_golfers &&
      formData.preferred_tee_time &&
      formData.first_name &&
      formData.last_name &&
      formData.email &&
      formData.course_id
    );
  }, [formData]);

  const onSubmitNotification = React.useCallback(async () => {
    if (isNotifyMeValid) {
      setProcessing(true);
      let response = await invoke("notifyMe", notifyMeData);

      if (response.status !== 200) {
        setProcessing(false);
        return;
      }
      if (response.success) {
        setShowSuccess(true);
      } else {
        setProcessing(false);
      }
    }
  }, [isNotifyMeValid, notifyMeData]);

  const onSubmitEnquiry = React.useCallback(async () => {
    if (isValid) {
      setProcessing(true);
      let data = {
        ...formData,
        tags: getMailingListTags(),
      };
      if (user?.id) {
        data = {
          ...data,
          email: user?.email,
        };
      }

      let response = await invoke("submitRateEnquiry", data);
      if (response.status !== 200) {
        setProcessing(false);
        return;
      }
      if (response.success) {
        setShowSuccess(true);
      } else {
        alert(
          "An error occurred. Please try again.  If the problem persists, please contact us."
        );
      }
      setProcessing(false);
    }
  }, [user, isValid, formData]);

  const disabledDate: RangePickerProps["disabledDate"] = (current) => {
    // Can not select days before today and today
    return current && current < dayjs().subtract(1, "day").endOf("day");
  };

  if (showSuccess) {
    return (
      <div className="px-6 py-9 bg-grey border border-grey3 rounded flex flex-col items-center gap-2">
        <CheckCircleIcon className="text-green w-12" />
        <Text
          size={21}
          weight="semibold"
          color="black90"
          leading={1.3}
          className="text-center"
        >
          {showForm ? "We'll be in touch!" : "Request Submitted"}
        </Text>

        <Text size={15} color="black80" leading={1.5} className="text-center">
          {closure ? (
            <>We'll notify you as soon as this course reopens for play.</>
          ) : (
            <>
              {showForm
                ? "Our support team will get back to you with your quote as soon as they can."
                : "We’ll let you know as soon as we have tee times available."}
            </>
          )}
        </Text>
      </div>
    );
  }

  if (!showForm) {
    return (
      <div className="px-6 py-9 bg-grey border border-grey3 rounded flex flex-col items-center gap-2">
        <Notify_Icon />
        <Text
          size={21}
          weight="semibold"
          color="black90"
          leading={1.3}
          className="text-center"
        >
          {closure
            ? closure.end_date
              ? "Get notified when tee times are available."
              : "Get notified when this course reopens for play"
            : "Get notified when tee times are available for this day"}
        </Text>
        <div className="flex flex-col gap-3">
          {closure ? (
            <>
              {" "}
              {closure.end_date ? (
                <>
                  <Text
                    size={15}
                    color="black80"
                    leading={1.5}
                    className="text-center"
                  >
                    This course is currently closed until{" "}
                    <span className="font-semibold">
                      {toFriendlyDate(
                        dayjs(closure.end_date)
                          .add(isEndOfDayClosure ? 1 : 0, "day")
                          .toString()
                      )}
                    </span>
                    , and we don’t currently have rates after this date.
                  </Text>
                  <Text
                    size={15}
                    color="black80"
                    leading={1.5}
                    className="text-center"
                  >
                    Please enter your email address below to get notified when
                    we have rates for this course.
                  </Text>
                </>
              ) : (
                <Text
                  size={15}
                  color="black80"
                  leading={1.5}
                  className="text-center"
                >
                  This golf course is currently closed until further notice.
                  Enter your email address below to get notified when/if tee
                  times are avaiable again.
                </Text>
              )}
            </>
          ) : (
            <>
              <Text
                size={15}
                color="black80"
                leading={1.5}
                className="text-center"
              >
                {last_contract_date &&
                dayjs(last_contract_date).isAfter(dayjs()) ? (
                  <>
                    The golf course has not yet released their tee sheets beyond{" "}
                    <span
                      className="font-semibold underline cursor-pointer"
                      onClick={() => {
                        if (last_contract_date)
                          setDate(
                            dayjs(last_contract_date).format("YYYY-MM-DD")
                          );
                      }}
                    >
                      {toFriendlyDate(last_contract_date)}
                    </span>
                  </>
                ) : (
                  <>We currently have no availability for this course.</>
                )}
              </Text>
              <Text
                size={15}
                color="black80"
                leading={1.5}
                className="text-center"
              >
                Enter your email address below to get notified as soon as tee
                times are available for this day.
              </Text>
            </>
          )}

          <Textbox
            id="email"
            type="email"
            value={notifyMeData?.email}
            placeholder="Enter your email address"
            onChange={(e) =>
              setNotifyMeData({
                ...notifyMeData,
                email: e,
              })
            }
          />
          {!user && (
            <Checkbox
              id="join_mailing_list"
              mode="small"
              label="Join our mailing list to get the best golf deals in your inbox"
              horizontallyCentered={false}
              onChange={(e) =>
                setNotifyMeData({
                  ...notifyMeData,
                  join_mailing_list: e,
                })
              }
              value={notifyMeData.join_mailing_list}
            />
          )}

          <Button
            onClick={onSubmitNotification}
            size="medium"
            loading={processing}
            disabled={!isNotifyMeValid}
            color="green"
            width="full"
            className={`!my-0`}
            text="Notify Me"
          />
        </div>
        {!closure && !hide_request_quote && (
          <div className="flex flex-col w-full items-center">
            <div className="relative inline-flex items-center justify-center w-full">
              <hr className="w-full h-px my-5 bg-grey3 border-0" />
              <span className="text-15 absolute px-3 italic text-black/40 -translate-x-1/2 bg-grey left-1/2">
                or
              </span>
            </div>
            <div
              onClick={() => {
                setShowForm(true);
              }}
            >
              <Text
                size={16}
                weight="semibold"
                color="black"
                leading={1.3}
                className="cursor-pointer hover:underline"
              >
                Request a quote
              </Text>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <Alert
        spacing="tight"
        iconAlign="center"
        type="warning"
        title="We don't currently have live rates at this course. If you would like us to secure you a tee time, please complete your information below."
      />
      <div className="flex flex-col gap-4">
        <div className="flex flex-col flex-1 gap-1">
          <Text size={13} weight="semibold" color="black60" leading={1.3}>
            Preferred date of play
          </Text>
          <DateField
            id="arrival_date"
            format="ddd, DD MMMM YYYY"
            size={"large"}
            onChange={(e) => {
              setFormData({
                ...formData,
                date: e,
              });
            }}
            value={formData?.date ? dayjs(formData?.date) : dayjs()}
          />
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col flex-1 gap-1">
            <Text size={13} weight="semibold" color="black60" leading={1.3}>
              Number of golfers
            </Text>
            <select
              id="number_of_golfers"
              value={formData.number_of_golfers}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  number_of_golfers: Number(e.target.value),
                })
              }
            >
              {_.times(10, (i) => (
                <option value={i + 1}>{i + 1}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col flex-1 gap-1">
            <Text size={13} weight="semibold" color="black60" leading={1.3}>
              Preferred tee time
            </Text>
            <Choice
              id="preferredTime"
              placeholder="Please select"
              value={formData.preferred_tee_time}
              options={preferredTimeOptions}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  preferred_tee_time: String(e),
                })
              }
            />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-2">
            <Text size={13} weight="semibold" color="black60" leading={1.3}>
              About you
            </Text>
            <Textbox
              id="first_name"
              value={formData?.first_name}
              autoFocus
              placeholder="First name"
              onChange={(e) =>
                setFormData({
                  ...formData,
                  first_name: e,
                })
              }
            />
            <Textbox
              id="last_name"
              value={formData?.last_name}
              placeholder="Last name"
              onChange={(e) =>
                setFormData({
                  ...formData,
                  last_name: e,
                })
              }
            />{" "}
            <Choice
              id="country_of_residence"
              placeholder="Country of Residence"
              searchable
              value={formData?.country_of_residence}
              options={COUNTRIES?.map((country_of_residence: any) => {
                return {
                  label: country_of_residence.text,
                  value: country_of_residence.value,
                };
              })}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  country_of_residence: String(e),
                })
              }
            />
            <Textbox
              id="email"
              type="email"
              value={formData?.email}
              placeholder="Email Address"
              onChange={(e) =>
                setFormData({
                  ...formData,
                  email: e,
                })
              }
            />
            {/* <Phone
              id="phone"
              value={formData?.phone}
              placeholder="Phone number"
              onChange={(e) =>
                setFormData({
                  ...formData,
                  phone: e,
                })
              }
            /> */}
            {!user && (
              <div className="pt-2 flex gap-3 flex-col">
                {/* <Checkbox
                  id="create_account"
                  label="Create an account to save your favourites, enjoy speedy checkout, and get access to member-only deals!"
                  horizontallyCentered={false}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      create_account: e,
                    })
                  }
                  value={formData.create_account}
                /> */}
                <Checkbox
                  id="join_mailing_list"
                  mode="small"
                  label="Join our mailing list to get the best golf deals in your inbox"
                  horizontallyCentered={false}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      join_mailing_list: e,
                    })
                  }
                  value={formData.join_mailing_list}
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col flex-1 gap-1">
          <Text size={13} weight="semibold" color="black60" leading={1.3}>
            Optional Message
          </Text>
          <Textbox
            id="message"
            value={formData?.message}
            multiline
            placeholder="Enter your message here"
            onChange={(e) =>
              setFormData({
                ...formData,
                message: e,
              })
            }
          />
        </div>

        <Button
          onClick={onSubmitEnquiry}
          size="medium"
          loading={processing}
          disabled={!isValid}
          color="green"
          width="full"
          className={`!my-0`}
          text="Submit"
        />
        <div className="flex flex-col w-full items-center mb-4">
          <div className="relative inline-flex items-center justify-center w-full">
            <hr className="w-full h-px my-5 bg-grey3 border-0" />
            <span className="text-15 absolute px-3 italic text-black/40 -translate-x-1/2 bg-white left-1/2">
              or
            </span>
          </div>
          <div
            onClick={() => {
              setShowForm(false);
            }}
          >
            <Text
              size={16}
              weight="semibold"
              color="black"
              leading={1.3}
              className="cursor-pointer hover:underline"
            >
              Notify me when tee times are available
            </Text>
          </div>
        </div>
      </div>
    </>
  );
};

export default RateEnquiry;
